import {
  LogCategoryEnum,
  LoggingLevel,
  ElasticIndices,
} from '../services/Enums/shared';
import { IUserFormProps } from '../components/Administration/Users/UserForm';
import { LocationWorkflowStepName } from '../components/WorkflowQueues/Location/presentation/config';
import { ILookupType } from '../components/LookupValues/common/defaults';
import { eValueType } from '../components/WorkflowQueues/Location/presentation/LocationEntry';
import { UserInfo } from '../components/Login/common/defaults';
import {
  INotificationTemplate,
  INotificationType,
} from '../components/NotificationTemplate/business-objects/common';

export enum NotificationStatus {
  CREATED = 1,
  QUEUED = 2,
  SENT = 3,
  REMOVED = 4,
}

export enum WorkflowStatus {
  Created = 1,
  Completed = 2,
  Skipped = 3,
  Reopened = 4,
  Assigned = 5,
}

export enum eLocationUserType {
  FromInvestigatorReport = 1,
  SpotMapping = 2,
  LocationReview = 3,
  Administrator = 4,
}

export interface IUser {
  userName: string;
  active: boolean;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  aadLogin: string;
}

export interface INotification {
  id: number;
  createdDate: Date;
  subject: string;
  body: string;
  createdBy: number;
  emailedDate: Date;
  internalId: number;
  notificationTemplate: INotificationTemplate;
  notificationTemplateId: number;
  notificationType: INotificationType;
  originalNotification: null;
  originalNotificationId: null;
  printedDate: null;
  sendFrom: null;
  sendTo: null;
  sentBy: null;
  sentDate?: Date;
  status: INotificationStatus;
  statusId: number;
  targetPersonId: null;
  targetUserId: null;
  triggeredBy: null;
}

export interface INotificationStatus {
  id: number;
  name: string;
  description: string;
}

export interface IAuditLogBase {
  elasticIndex: ElasticIndices;
  logLevel: LoggingLevel;
  logCategory: LogCategoryEnum;
}

export interface IPaperReport {
  id: number;
  documentId: string;
  pdf?: [];
  active: boolean;
}

export interface IGeneralInformation {
  crashDateTime?: Date;
  agency?: IAgency;
}

export interface ICrashLocation {
  county?: ICounty;
  streetNameOrHighwayNumber?: string;
}

export interface ICrashReport {
  id: string;
  active: boolean;
  vehicle: IVehicle[];
  generalInformation: IGeneralInformation;
  crashLocation: ICrashLocation;
}

export interface IReport {
  id: number;
  crashReportId?: number;
  paperReportId?: number;
  paperReport?: IPaperReport;
  crashReport?: ICrashReport;
  keywordsId?: number;
  reportStatusId?: number;
  crashKey: string;
  documentId: string;
  mergeStatusId: number;
  mergeStatus: IMergeStatus;
  keywords?: IKeywords;
  farsCrashData?: IFARSData;
  reportDraft?: IReportDraftDomain;
  reportsMergedToThis?: IReport[];
  supplementedReports?: IReport[];
  createdDate?: Date;
  comments?: IComment[];
  mergedToReportId?: number;
  groupingKey?: string;
  pendingMerge?: boolean;
  completedDate?: Date;
  hasMergedDriverReport?: boolean;
  splitFromReportId?: number;
  splitFromReport?: IReport;
}

export interface IReportDraftDomain {
  id?: number;
  reportId?: number;
  reportData?: string;
  reportValidationData?: string;
}

export interface IMergeStatus {
  name: string;
  id: number;
}

export interface IReportType {
  id: number;
  name: string;
  description: string;
}

export interface IKeywords {
  id: number;
  reportType?: IReportType;
  agencyCaseNumber?: string;
  numberOfFatalities?: string;
  agency?: IAgency;
  agencyId?: number;
  countyId?: number;
  county?: ICounty;
  city?: string;
  driver1Name?: string;
  highwayNumber?: string;
  highwayType?: string;
  dateOfCrash?: string | Date;
  createdById: number;
  createdDate: string;
  modifiedById: number;
  modifiedDate: string;
  crashKey?: string;
  originalCrashKey?: string;
  documentId?: string;
  involvedParties?: IInvolvedParty[];
  searchResults?: ISearchResults[];
  injuryStatus?: string;
  nonMotoristIndicator?: string;
  truckBusIndicator?: string;
  workZoneIndicator?: string;
  propertyDamageIndicator: string;
  reportTypeId?: number;
  narrativeDiagramPages?: string;
  priority?: string;
  onBasePriorityNumber?: number;
  priorityName?: string;
  priorityNumber?: number;
  mergeStatusId?: number;
}

export interface IAgency {
  id: number;
  createdById: number;
  createdDate: Date;
  modifiedById: number;
  modifiedDate: Date;
  name: string;
  description: string;
  ori: string;
  vendorId?: number;
  email?: string;
  investigatorTypeId?: number;
}

export interface IInvolvedParty {
  firstName: string;
  lastName: string;
  businessName: string;
  id?: number;
  keywordsId?: number;
  businessOwned?: boolean;
}

export interface ISearchResults {
  crashKey: string;
  firstName: string;
  lastName: string;
  county: string;
  crashDate: number;
}

export interface ICounty {
  id: number;
  name: string;
  description: string;
}

export interface IWorkflowItemStep {
  workflowItem: IWorkflowItem;
  user: IUser;
  id: number;
  workflowStepStatus: IWorkflowStepStatus;
  workflowStep: IWorkflowStep;
  workflowItemId: number;
  assignedTo: number;
  statusId: number;
  workflowStepId: number;
  completedDate: any;
  completedBy: number;
  startedDate: any;
  startedBy: number;
}

export interface IWorkflowStepStatus {
  name: string;
  description: string;
  id: number;
}

export interface IWorkflowStep {
  id: number;
  name: string;
  isOptional: boolean;
  stepNumber: number;
  workflowId: number;
  hasHoldOnComplete: boolean;
  formIoFormId: string;
}

export interface IWorkflowItem {
  report?: IReport;
  reportId?: number;
  id: number;
}

// Based off fo React Router Dom's location:
// https://github.com/ReactTraining/react-router/blob/master/packages/react-router/docs/api/location.md
export interface IAppRouteLocation {
  key?: string;
  pathname: string;
  search?: string;
  hash?: string;
  state?: any;
}

export interface IMapConfig {
  reportId: number;
  privateProperty: boolean;
  unknownLocation: boolean;
  currentUserId: number;
  webServiceUrl: string;
  defaultLocations: any[];
  onSaveValue: (
    data: any,
    privateProperty: boolean,
    unknownLocation: boolean
  ) => void;
  readonly: boolean;
  userTypeId: number;
  crashReportId: number;
  defaultFocusLocation?: any;
  locationQueue: LocationWorkflowStepName;
  getRecordValueByKey: (key: string) => string;
  setRecordValue: (key: string, value: string, valueType: eValueType) => void;
  setCorrectedValueEditability: (key: string, value: boolean) => void;
}

export interface IFormioValidationError {
  components: any[];
  message: string;
  paths: any[];
  type: string;
}

export interface IExtract {
  id: number;
  createdDate: Date;
  startDate: Date;
  endDate: Date;
  data: string;
  crashKey: string;
  isComplete: boolean;
  extractTypeId: number;
  extractType: IExtractType;
  extractLogs: IExtractLog[];
  extractSubType?: IExtractSubType;
  selectedDatabase?: IStandardSummaryDatabase;
  databaseId?: number;
}
export interface IExtractLog {
  id: number;
  createdDate: Date;
  dataGeneratedDate: Date;
  sentToExperianDate: Date;
  sentToCarfaxDate: Date;
  sentToTruckBusDate: Date;
  sentToNhtsa: Date;
  sentToLawFirm: Date;
  comment: string;
  errorMessage: string;
}
export interface IExtractType {
  id: number;
  name: string;
  description: string;
  extractSubTypes: IExtractSubType[];
}

export interface IExtractSubType {
  id: number;
  name: string;
  description: string;
  extractTypeId: string;
}

export interface IStandardSummaryType {
  id: number;
  name: string;
  description: string;
}
export interface IStandardSummaryDatabase {
  id: number;
  name: string;
  // connectionString - client doens't want to see that
}
export interface IStandardSummary {
  id: number;
  createdDate: Date;
  startDate: Date;
  endDate: Date;
  data: string;
  crashKey: string;
  isComplete: boolean;
  monthNumber?: number;
  quarter?: number;
  year?: number;
  standardSummaryTypeId: number;
  standardSummaryType: IStandardSummaryType;
  standardSummaryLogs: IStandardSummarytLog[];
}

export interface IStandardSummaryProgress {
  standardSummaryId: number;
  totalPartsCount: number;
  completedPartsCount: number;
  isCompleted: boolean;
}

export interface IStandardSummarytLog {
  id: number;
  createdDate: Date;
  dataGeneratedDate: Date;
  sentToExperianDate: Date;
  sentToCarfaxDate: Date;
  sentToTruckBusDate: Date;
  sentToNhtsa: Date;
  sentToLawFirm: Date;
  comment: string;
  errorMessage: string;
}

export interface IRole {
  id: number;
  name: string;
  description: string;
}

export interface IComment {
  id: number;
  text: string;
  createdDate?: Date;
  createdBy?: UserInfo;
  createdById?: number;
  commentViews?: ICommentView[];
  reportId: number;
}

export interface ICommentView {
  id: number;
  commentId: number;
  viewedById?: number;
  viewedBy?: UserInfo;
  viewedDate?: Date;
}

export interface IFarsQueueItem {
  farsCrashData: IFARSData;
  farsJson: any;
}

export interface IFARSData {
  caseNumber: number;
  dateOfCrash: string;
  crashLocation: number;
  fatality: number;
  crashType: number;
  accidentType: number;
  occupants: IFARSPersonData[];
  nonMotorists: IFARSPersonData[];
  isActive: boolean;
  id?: number;
  farsRefCrashLocation?: IFarsRef;
  farsRefCollisionType?: IFarsRef;
  farsCrashTypes?: IFarsCrashType[];
  fatalities?: number;
  crashKey?: string;
  isSent: boolean;
}
export interface IFarsCrashType {
  farsCrashDataId: number;
  farsRefCrashType: IFarsRef;
}
export interface IFARSPersonData {
  Occupant?: string;
  Age?: string;
  PedestrianAge?: number;
  NonMotoristAge?: number;
  Restraints?: string;
  DeathCertificate: string;
  DeathCertificateNumber?: number;
  DateOfDeath: string;
  TimeOfDeath: string;
  RaceEthnicity: number;
  InjuryAtWork: string;
  BloodAlcoholContent: number;
  AgeUnknown?: boolean;
}

export interface IFarsRef {
  code: number;
  description: string;
}

export interface IDocumentViewerItem {
  reportId: number;
  data?: string;
  frontSheetData?: string;
  pdfDifferences?: string;
  reportType: IReportType;
  createdDate: Date;
  crashKey: string;
  originalCrashKey: string;
  documentId: string;
  comparedToCrashKey?: string;
  report?: IReport;
  fileURL?: string;
  source?: string;
  frontSheetSource?: string;
  sortOrder: number;
}

export interface IVehicle {
  id: number;
  driver: IDriver;
  occupant: IOccupant[];
}
export interface IDriver {
  driverInfo: IDriverInfo;
}

export interface IDriverInfo {
  driversLicense: IDriversLicense;
}
export interface IDriversLicense {
  number: string;
}

export interface IOccupant {
  id: number;
  personInfo: IPersonInfo;
}

export interface IPersonInfo {
  id: number;
  name: IName;
  birthDate: IBirthDate;
  age: string;
  personType: string;
  incidentResponder: string;
  incidentResponderType: string;
  injurySeverity: string;
  telephone: string;
}

export interface IName {
  id: number;
  firstName: string;
  middleName: string;
  lastName: string;
  nameSuffix: string;
  lastFirstMiddle: string;
}

export interface IBirthDate {
  id: number;
  birthYear: number;
  birthMonth: number;
  birthDay: number;
  format224: string;
  birthDateTime: Date;
}

export interface IAdvancedSearchStore {
  id: string;
  name: string;
  description?: string;
  modelId: string;
  userId: number;
  queryJson: string;
  createdDate: Date;
  lastModifiedDate: Date;
  advancedSearchShares: IAdvancedSearchShare[];
}

export interface IAdvancedSearchShare {
  advancedSearchStoreId: string;
  sharedUserId: number;
  sharedUser: UserInfo;
}

export interface IExportColumnMap {
  columnName: string;
  propertyName: string;
  convertMethod?: (value: any) => string;
}

export class PagedResultsRequest {
  public itemsPerPage: number;
  public pageNumber: number;
  public searchCriteria?: string;
  public orderByField?: string;
  public orderDescending: boolean;
}

export interface IPageResultsResponse {
  totalResults?: number;
}

export interface IDataQueueResponse {
  id?: number;
  dateSubmitted?: Date;
  workflowStatus?: string;
  assignedTo?: string;
  documentId?: string;
  county?: string;
  priority?: string;
  priorityNumber?: number;
  crashKey?: string;
  originalCrashKey?: string;
  agencyCaseNumber?: string;
  crashDate?: Date;
  reportType?: string;
  reportId?: number;
  crashId?: number;
  reportTypeId?: number;
  mergedToReportId?: number;
  supplementedByReportId?: number;
  reportsMergedToThis?: IReport[];
  supplementedReports?: IReport[];
  pendingMerge?: boolean;
  completedDate?: Date;
  hasMergedDriverReport?: boolean;
}
export class SearchQueuePagedRequest extends PagedResultsRequest {
  public assignedOnly: boolean;
  public userOnly: boolean;
  public startDate?: string;
  public endDate?: string;
  public workflowItemStepId?: number;
  public workflowStatus?: number;
  public queue?: number;
  public includeNewDriverReports: boolean;
  public unassignedOnly: boolean;
}

export interface ISearchQueuePagedResponse extends IPageResultsResponse {
  workflowItems?: IDataQueueResponse[];
}
